/**
 * Utility functions for formatting addresses in ENS-style format
 * Converts addresses to {handle}.pay.eth format when user has an ETH address
 */

/**
 * Check if an address is a valid Ethereum address
 */
export function isEthereumAddress(address: string): boolean {
  if (!address) return false;
  // Basic Ethereum address validation (0x followed by 40 hex characters)
  return /^0x[a-fA-F0-9]{40}$/.test(address);
}

/**
 * Convert a handle to ENS-style format
 * @param handle - The user's handle (e.g., "pr0", "tttt")
 * @returns The ENS-style address (e.g., "pr0.pay.eth")
 */
export function handleToEnsStyle(handle: string): string {
  if (!handle) return '';
  return `${handle}.pay.eth`;
}

/**
 * Check if user has an ETH address (which enables .pay.eth for all their addresses)
 * @param userAddresses - Object containing all user's addresses
 * @returns true if user has an ETH address
 */
export function hasEthAddress(userAddresses: { [key: string]: string }): boolean {
  return !!(userAddresses && userAddresses['ETH'] && isEthereumAddress(userAddresses['ETH']));
}

/**
 * Get the display format for an address
 * Shows .pay.eth format if user has an ETH address, regardless of the coin
 * @param address - The cryptocurrency address
 * @param coinSymbol - The coin symbol (e.g., 'BTC', 'ETH', 'USDC')
 * @param handle - The user's handle
 * @param userAddresses - All of the user's addresses (to check if they have ETH)
 * @returns Display information with primary and secondary text
 */
export function getDisplayAddress(
  address: string,
  coinSymbol: string,
  handle: string,
  userAddresses: { [key: string]: string }
): {
  primary: string;           // Main display text (.pay.eth if user has ETH)
  secondary?: string;        // Secondary text (actual address)
  showEnsStyle: boolean;     // Whether .pay.eth format is shown
  ensAddress: string;        // The .pay.eth address
} {
  const ensAddress = handleToEnsStyle(handle);
  const showEnsStyle = hasEthAddress(userAddresses);

  if (showEnsStyle) {
    return {
      primary: ensAddress,
      secondary: address,
      showEnsStyle: true,
      ensAddress,
    };
  }

  return {
    primary: address,
    showEnsStyle: false,
    ensAddress,
  };
}

/**
 * Shorten an address for display
 * @param address - The full address
 * @param prefixLength - Number of characters to show at start (default: 6)
 * @param suffixLength - Number of characters to show at end (default: 4)
 * @returns Shortened address (e.g., "0x1234...5678")
 */
export function shortenAddress(
  address: string,
  prefixLength: number = 6,
  suffixLength: number = 4
): string {
  if (!address) return '';
  if (address.length <= prefixLength + suffixLength) {
    return address;
  }
  return `${address.slice(0, prefixLength)}...${address.slice(-suffixLength)}`;
}

/**
 * Format address for display with both ENS and shortened address
 * @param address - The cryptocurrency address
 * @param handle - The user's handle
 * @param userAddresses - All of the user's addresses
 * @returns Formatted string like "handle.pay.eth (0x1234...5678)"
 */
export function formatAddressDisplay(
  address: string,
  handle: string,
  userAddresses: { [key: string]: string }
): string {
  if (hasEthAddress(userAddresses)) {
    return `${handleToEnsStyle(handle)} (${shortenAddress(address)})`;
  }
  return address;
}

/**
 * Check if a coin is Ethereum-based (ETH or ERC20)
 * @param coinInfo - Coin information object from coins.json
 * @returns true if coin is ETH-based
 */
export function isEthBasedCoin(coinInfo: any): boolean {
  if (!coinInfo) return false;
  return coinInfo.symbol === 'ETH' || coinInfo.ERC20 === true;
}

/**
 * Check if user has any ETH or ERC20 tokens (which enables .pay.eth ENS)
 * @param userAddresses - Object containing all user's addresses
 * @param coinsData - Coins data from coins.json
 * @returns true if user has at least one ETH or ERC20 token
 */
export function hasEthBasedAddress(
  userAddresses: { [key: string]: string },
  coinsData: { [key: string]: any }
): boolean {
  if (!userAddresses || !coinsData) return false;

  // Check if user has ETH
  if (userAddresses['ETH'] && isEthereumAddress(userAddresses['ETH'])) {
    return true;
  }

  // Check if user has any ERC20 tokens
  for (const coinSymbol in userAddresses) {
    const coinInfo = coinsData[coinSymbol];
    if (coinInfo && coinInfo.ERC20 === true) {
      return true;
    }
  }

  return false;
}
